import SEO from "@/components/SEO";
import { Reveal, PractitionerCard } from "@/components/Shared";
import { HairHero, HairBreadcrumb, HairBand, HairEyebrow, HairHeading, Bullets, Paras, HairRiskPanel, HairCTABand } from "@/components/HairShared";
import { StickyCTA } from "@/components/Landing";
import { OFFERS } from "@/data/landing";
import { getTeamMember } from "@/data/content";
import { HAIR_BREADCRUMBS, HAIR_HERO, ASSESSMENT_PAGE } from "@/data/hair";

const HairAssessment = () => {
  const p = ASSESSMENT_PAGE;
  const crumbs = HAIR_BREADCRUMBS.treatment();
  return (
    <>
      <SEO
        title="Hair Assessment and Medical Treatment | Lumière Hair Clinic, Cottesloe"
        description="A doctor-run hair assessment in Cottesloe: history, examination, dermatoscopy and a written plan. Diagnosis before any treatment."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: `https://www.lumiereaesthetics.com.au${c.to}` })),
        }}
      />
      <HairHero eyebrow={p.eyebrow} title={p.title} standfirst={p.standfirst} offer={OFFERS.hair} image={HAIR_HERO.room} tall={false} testId="hair-assessment-hero" />
      <HairBreadcrumb items={crumbs} />

      <HairBand narrow testId="assessment-what-happens">
        <Reveal>
          <HairEyebrow className="mb-4">{p.whatHappens.heading}</HairEyebrow>
          <Paras items={[p.whatHappens.intro]} />
          <div className="mt-6"><Bullets items={p.whatHappens.items} /></div>
          <p className="text-ink/85 leading-[1.8] text-[17px] mt-6">{p.whatHappens.outro}</p>
        </Reveal>
      </HairBand>

      <HairBand dark narrow testId="assessment-medical">
        <Reveal>
          <HairEyebrow className="mb-4">{p.medical.heading}</HairEyebrow>
          <Paras items={p.medical.paras} dark />
          <div className="bg-hair-panel relative p-8 mt-8" data-testid="hair-rx-pricing-panel">
            <div className="gold-frame inset-3 opacity-70" aria-hidden="true" />
            <p className="text-warm-white/85 leading-[1.8] relative text-[15px]">{p.medical.rxNote}</p>
          </div>
        </Reveal>
      </HairBand>

      <HairBand narrow testId="assessment-whole-answer">
        <Reveal>
          <HairEyebrow className="mb-4">{p.wholeAnswer.heading}</HairEyebrow>
          <Paras items={[p.wholeAnswer.body]} />
        </Reveal>
      </HairBand>

      <HairBand dark testId="assessment-who-to-see">
        <Reveal>
          <HairEyebrow className="mb-4">{p.whoToSee.heading}</HairEyebrow>
          <p className="text-warm-white/85 leading-[1.8] text-[17px] mt-4 max-w-3xl">{p.whoToSee.note}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8 max-w-3xl">
            {p.whoToSee.practitioners.map((id, i) => (
              <PractitionerCard key={id} member={getTeamMember(id)} index={i} />
            ))}
          </div>
        </Reveal>
      </HairBand>

      <HairBand narrow testId="assessment-risks">
        <Reveal><HairRiskPanel risks={p.risks} /></Reveal>
      </HairBand>

      <HairBand dark narrow testId="assessment-cost">
        <Reveal>
          <HairEyebrow className="mb-4">{p.cost.heading}</HairEyebrow>
          <HairHeading dark>What it costs to begin</HairHeading>
          <p className="text-warm-white/85 leading-[1.8] text-[17px] mt-6" data-testid="assessment-cost-body">{p.cost.body}</p>
        </Reveal>
      </HairBand>

      <StickyCTA practitioner="Dr Kristy Truong" hair />
      <HairCTABand />
    </>
  );
};

export default HairAssessment;
